import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { IDateRangePreset, TDateRangePresetId } from "../interfaces";
import { DEMO_ACCOUNTS, getPresets, LOCAL_STORAGE_KEYS } from "../shared/constants";
import dayjs from "dayjs";
import { useAuth } from "../hooks";
import { DateRangeContext } from "./DateRangeContextDefinition";

export const DateRangeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedPreset, setSelectedPreset] = useState<IDateRangePreset | undefined>(() => {
    try {
      const storedPreset = localStorage.getItem(
        LOCAL_STORAGE_KEYS.DATA_RANGE_PRESET
      ) as TDateRangePresetId | null;
      if (storedPreset) {
        const found = getPresets(t).find((p) => p.id === storedPreset);
        if (found) return found;
      }
    } catch { /* empty */ }

    const demoAccounts = DEMO_ACCOUNTS.includes(String(user?.schema));
    const presetType = demoAccounts ? "lastMonth" : "last30Days";

    const presetId = searchParams.get("preset") as TDateRangePresetId;
    if (presetId) {
      return getPresets(t).find((p) => p.id === presetId);
    }

    return getPresets(t).find((p) => p.id === presetType);
  });

  const [dateRange, setDateRangeState] = useState<{ startDate: Date; endDate: Date }>(() => {
    // If a preset is active (and not custom), recalculate from it so relative ranges stay current.
    if (selectedPreset && selectedPreset.id !== "custom") {
      const [s, e] = selectedPreset.getRange();
      return { startDate: s as Date, endDate: e as Date };
    }

    try {
      const lsFrom = localStorage.getItem(LOCAL_STORAGE_KEYS.DATA_RANGE_FROM);
      const lsTo = localStorage.getItem(LOCAL_STORAGE_KEYS.DATA_RANGE_TO);
      if (lsFrom && lsTo) {
        const fromValid = dayjs(lsFrom, "YYYY-MM-DD", true).isValid();
        const toValid = dayjs(lsTo, "YYYY-MM-DD", true).isValid();
        if (fromValid && toValid) {
          return { startDate: new Date(lsFrom), endDate: new Date(lsTo) };
        }
      }
    } catch { /* empty */ }

    const from = searchParams.get("from");
    const to = searchParams.get("to");
    if (from && to) {
      return { startDate: new Date(from), endDate: new Date(to) };
    }

    return { startDate: new Date(), endDate: new Date() };
  });

  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    params.set("from", dayjs(dateRange.startDate).format("YYYY-MM-DD"));
    params.set("to", dayjs(dateRange.endDate).format("YYYY-MM-DD"));
    if (selectedPreset) {
      params.set("preset", selectedPreset.id);
    } else {
      params.delete("preset");
    }
    setSearchParams(params, { replace: true });

    try {
      localStorage.setItem(LOCAL_STORAGE_KEYS.DATA_RANGE_FROM, dayjs(dateRange.startDate).format("YYYY-MM-DD"));
      localStorage.setItem(LOCAL_STORAGE_KEYS.DATA_RANGE_TO, dayjs(dateRange.endDate).format("YYYY-MM-DD"));
      if (selectedPreset) {
        localStorage.setItem(LOCAL_STORAGE_KEYS.DATA_RANGE_PRESET, selectedPreset.id);
      } else {
        localStorage.removeItem(LOCAL_STORAGE_KEYS.DATA_RANGE_PRESET);
      }
    } catch { /* empty */ }
  }, [dateRange, searchParams, selectedPreset, setSearchParams]);

  const setDateRange = (startDate: Date, endDate: Date) => {
    setDateRangeState({ startDate, endDate });
    setSelectedPreset(undefined);
  };

  const value = useMemo(
    () => ({ dateRange, selectedPreset, setDateRange, setSelectedPreset }),
    [dateRange, selectedPreset]
  );

  return (
    <DateRangeContext.Provider value={value}>
      {children}
    </DateRangeContext.Provider>
  );
};
